import { Col, Input, Row, Typography, Image } from 'antd';
import { useTranslation } from 'react-i18next';
import { BLACK, GRAY4 } from '../../../../constants/color';
import {
  INVOICE_REMARK_FONTSIZE,
  PRINTOUT_TERMS_FONT_SIZE,
} from '../../../../constants/printoutConstants';
import { DEFAULT_FONT_SIZE } from '../../../../constants/systemConstants';
import {
  PrintoutOrderBodyData,
  PrintoutOrderHeaderData,
} from '../../../../types';
import getDashboardStyle from '../../../../utils/getDashboardStyle';
import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

type InvoicePrintoutBodyProps = {
  header?: PrintoutOrderHeaderData;
  body: PrintoutOrderBodyData;
};

const InvoicePrintoutBody = ({ header, body }: InvoicePrintoutBodyProps) => {
  const { t } = useTranslation();
  const [field, setField] = useState<{ [key: string]: string }>({});
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const { billName, billAddress, billPhone } = body;
    setField({
      billName: billName ? billName : '',
      billAddress: billAddress ? billAddress : '',
      billPhone: billPhone ? billPhone : '',
    });
  }, [body]);

  return (
    <div style={{ flexDirection: 'row' }}>
      {getDashboardStyle().isWholeSaleAppType && (
        <Row /**Rules, Buyer/Seller Remark*/>
          <Col /**Rules */
            span={15}
            style={{
              width: '100%',
              flexDirection: 'column',
            }}
          >
            <Typography.Text
              style={{ fontWeight: 'bold', fontSize: PRINTOUT_TERMS_FONT_SIZE }}
            >
              {t('order.printoutComponents.shippingTermsTitle') + ':'}
            </Typography.Text>
            {body.shippingTerms.map((term, index) => {
              return (
                <Typography.Paragraph
                  key={index}
                  style={{
                    margin: 0,
                    fontSize: PRINTOUT_TERMS_FONT_SIZE,
                  }}
                >{`${index + 1}. ${term}`}</Typography.Paragraph>
              );
            })}
          </Col>
          {getDashboardStyle().isWholeSaleAppType &&
            ((header && header.taskQrCodeUrl) || body.specialUserIcon) && (
              <Col /**QR CODE - for sortation - only display in wholesale */
                span={9}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 10,
                }}
              >
                {header && header.taskQrCodeUrl && <QRCodeSVG
                  value={header.taskQrCodeUrl}
                  size={100}
                  imageSettings={{
                    src: header.logo ? header.logo : '',
                    height: 10,
                    width: 10,
                    excavate: true,
                  }}
                />}
                {body.specialUserIcon && (
                  <Image style={{ width: 100 }} src={body.specialUserIcon} />
                )}
              </Col>
            )}
          {(body.buyerRemark || body.sellerRemark || body.remarkOnInvoice) && (
            <Col span={24} style={{ marginTop: 10 }}>
              <div
                style={{
                  flexDirection: 'column',
                  display: 'flex',
                }}
              >
                {body.remarkOnInvoice && (
                  <Typography.Text
                    style={{
                      fontSize: INVOICE_REMARK_FONTSIZE,
                      color: BLACK,
                      fontWeight: 600,
                    }}
                  >
                    {`${t('order.printoutComponents.remarkOnInvoiceTitle')}: 【${
                      body.remarkOnInvoice
                    }】`}
                  </Typography.Text>
                )}
                {body.buyerRemark && (
                  <Typography.Text
                    style={{
                      fontSize: INVOICE_REMARK_FONTSIZE,
                      color: BLACK,
                      fontWeight: 600,
                    }}
                  >
                    {`${t('order.printoutComponents.buyerRemarkTitle')}: ${
                      body.buyerRemark
                    }`}
                  </Typography.Text>
                )}
                {body.sellerRemark && (
                  <Typography.Text
                    style={{
                      fontSize: INVOICE_REMARK_FONTSIZE,
                      color: BLACK,
                      fontWeight: 600,
                    }}
                  >
                    {`${t('order.printoutComponents.sellerRemarkTitle')}: ${body.sellerRemark}`}
                  </Typography.Text>
                )}
              </div>
            </Col>
          )}
        </Row>
      )}
      <Row /**Delivery To, Bill To, Details, Payment */
        style={{
          marginTop: 50,
          justifyContent: 'space-between',
        }}
        gutter={[16, 16]}
      >
        <Col /**Delivery To */
          span={6}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div
            style={{
              marginTop: 5,
              marginBottom: 5,
              height: 2,
              backgroundColor: GRAY4,
            }}
          />
          <Typography.Paragraph
            strong
            style={{
              fontSize: DEFAULT_FONT_SIZE,
              color: BLACK,
            }}
          >
            {t('order.printoutComponents.deliveryTo')}
          </Typography.Paragraph>
          <Typography.Text
            style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
          >
            {body.consignee}
          </Typography.Text>
          <Typography.Text
            style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
          >
            {body.fullAddress}
          </Typography.Text>
          <Typography.Text
            style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
          >
            {body.mobile}
          </Typography.Text>
        </Col>
        <Col /**Bill To */
          span={6}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div
            style={{
              marginTop: 5,
              marginBottom: 5,
              height: 2,
              backgroundColor: GRAY4,
            }}
          />
          <Typography.Paragraph
            strong
            style={{
              fontSize: DEFAULT_FONT_SIZE,
              color: BLACK,
            }}
          >
            {t('order.printoutComponents.billTo')}
          </Typography.Paragraph>
          {field &&
            Object.keys(field).length > 0 &&
            Object.keys(field).map((key) => (
              <div key={key}>
                {isEditing[key] ? (
                  <Input
                    autoFocus
                    defaultValue={field[key]}
                    onChange={(e) =>
                      setField((prev) => ({
                        ...prev,
                        [key]:
                          e.target.value ||
                          body[key as keyof typeof InvoicePrintoutBody],
                      }))
                    }
                    onBlur={() => setIsEditing({ [key]: false })}
                  />
                ) : (
                  <Typography.Text
                    style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
                    onClick={() => setIsEditing({ [key]: true })}
                  >
                    {field[key]}
                  </Typography.Text>
                )}
              </div>
            ))}
        </Col>
        <Col /**Details */
          span={6}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div
            style={{
              marginTop: 5,
              marginBottom: 5,
              height: 2,
              backgroundColor: GRAY4,
            }}
          />
          <Typography.Paragraph
            strong
            style={{
              fontSize: DEFAULT_FONT_SIZE,
              color: BLACK,
            }}
          >
            {t('order.printoutComponents.details')}
          </Typography.Paragraph>

          {body.user && body.user.nickName && (
            <Typography.Text
              style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
            >
              {`${t('order.printoutComponents.name')} : ${body.user.nickName}`}
            </Typography.Text>
          )}
          <Typography.Text
            style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
          >
            {`${t('order.printoutComponents.orderSn')}: ${body.orderSn}`}
          </Typography.Text>
          {getDashboardStyle().isWholeSaleAppType && body.invoiceSn && (
            <Typography.Text
              style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
            >
              {`${t('order.printoutComponents.invoiceSn')}: ${body.invoiceSn}`}
            </Typography.Text>
          )}
        </Col>
        <Col /**Other */
          span={6}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div
            style={{
              marginTop: 5,
              marginBottom: 5,
              height: 2,
              backgroundColor: GRAY4,
            }}
          />
          <Typography.Paragraph
            strong
            style={{
              fontSize: DEFAULT_FONT_SIZE,
              color: BLACK,
            }}
          >
            {t('order.printoutComponents.more')}
          </Typography.Paragraph>
          <Typography.Text
            style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
          >
            {`${t('order.printoutComponents.payName')} : ${body.payName}`}
          </Typography.Text>
          {body.payTime && (
            <Typography.Text
              style={{ fontSize: DEFAULT_FONT_SIZE - 2, color: BLACK }}
            >
              {`${t('order.printoutComponents.payTime')} : ${body.payTime}`}
            </Typography.Text>
          )}
          {body.orderShipping && body.orderShipping.shippingName && (
            <Typography.Text style={{ fontSize: DEFAULT_FONT_SIZE - 2 }}>
              {`${t('order.printoutComponents.shippingName')} : ${
                body.orderShipping.shippingName
              }`}
            </Typography.Text>
          )}
          {body.orderShipping &&
            body.orderShipping.formattedExpectShippingTime && (
              <Typography.Paragraph style={{ fontSize: DEFAULT_FONT_SIZE - 2 }}>
                {`${t('order.printoutComponents.expectShippingTimeName')} : ${
                  body.orderShipping.formattedExpectShippingTime
                }`}
              </Typography.Paragraph>
            )}
        </Col>
      </Row>
    </div>
  );
};

export default InvoicePrintoutBody;
